/**
 * /marketplace/shop — TravelBio Shop landing (Coming Soon).
 *
 * Public route (matches the pattern used by
 * MarketplaceTravelInsurancePage): no auth gate so search engines
 * index it and anon visitors can land here from external links.
 * Uses the same top-bar / hero / footer shell as the other
 * marketplace pages so navigation stays visually consistent.
 *
 * 2026-07-24 — placeholder only, no email capture / no CTA.
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShoppingBag } from "lucide-react";

const PAGE_TITLE = "TravelBio Shop — Coming Soon | TravelBio";
const PAGE_DESC =
  "Luggage tags, physical journals, and more travel merch — coming soon to the TravelBio Shop.";

function useDocumentHead() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", PAGE_DESC);
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
    };
  }, []);
}

export default function MarketplaceShopPage() {
  useDocumentHead();

  return (
    <div className="min-h-screen bg-white" data-testid="marketplace-shop">
      {/* Top bar — mirrors MarketplaceTravelInsurancePage exactly. */}
      <div className="sticky top-0 z-30 bg-tb-navy-deep border-b-2 border-tb-gold/70 px-4 py-2 flex items-center gap-3">
        <Link
          to="/"
          data-testid="marketplace-back"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-tb-gold/50 text-tb-cream hover:bg-tb-gold/10 tb-tap"
          style={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.85rem" }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to TravelBio
        </Link>
        <span className="font-serif-it italic text-tb-gold-bright text-lg tracking-wide ml-2">
          Marketplace &middot; Shop
        </span>
      </div>

      {/* Centered "Coming Soon" block. */}
      <section className="max-w-2xl mx-auto px-4 py-24 md:py-32 text-center">
        <div
          aria-hidden="true"
          className="mx-auto w-16 h-16 rounded-full bg-tb-navy/5 flex items-center justify-center"
        >
          <ShoppingBag className="w-8 h-8 text-tb-navy" strokeWidth={1.5} />
        </div>
        <h1
          data-testid="marketplace-shop-title"
          className="mt-6 font-serif-it italic text-tb-navy leading-[1.05]"
          style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}
        >
          Coming Soon
        </h1>
        <p
          data-testid="marketplace-shop-subtitle"
          className="mt-4 font-hand text-tb-navy/70 text-base md:text-lg leading-relaxed max-w-lg mx-auto"
        >
          Luggage tags, physical journals, and more travel merch — coming
          soon to the TravelBio Shop.
        </p>
      </section>

      <footer className="bg-tb-navy text-tb-cream/80 border-t-2 border-tb-gold/70 px-4 py-6 text-center">
        <p className="font-hand text-tb-gold/40 text-xs">
          &copy; TravelBio &middot; your lifelong travel journal
        </p>
      </footer>
    </div>
  );
}
